import React, { useState, useEffect } from "react";
import { api, BACKEND_URL } from "../lib/api";

// Three-tier logo resolution for a partner, matching user's chosen behaviour:
//   1. If the partner has an uploaded logo (logo_url on the record) → use it (served via /api/partners/{id}/logo-image).
//   2. Else try the free, no-key Clearbit domain-logo endpoint using the partner's email domain.
//   3. Else fall back to a deterministic, nicely-tinted initials avatar.
// Each step gracefully degrades on error (onError) — the component never shows a broken image icon.

const PALETTE = [
  // Blue Kite navy family + supporting tones (readable on white, same vibe across the app)
  { bg: "#0B2545", fg: "#ffffff" }, // navy
  { bg: "#13315C", fg: "#ffffff" },
  { bg: "#1D3557", fg: "#ffffff" },
  { bg: "#2563EB", fg: "#ffffff" }, // blue
  { bg: "#0369A1", fg: "#ffffff" }, // sky-700
  { bg: "#0E7490", fg: "#ffffff" }, // cyan-700
  { bg: "#115E59", fg: "#ffffff" }, // teal-700
  { bg: "#C2410C", fg: "#ffffff" }, // orange-700 (brand accent)
  { bg: "#B45309", fg: "#ffffff" }, // amber-700
  { bg: "#7C3AED", fg: "#ffffff" }, // violet-600
  { bg: "#BE185D", fg: "#ffffff" }, // pink-700
];

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] || "").join("").toUpperCase() || name[0].toUpperCase();
}

function domainFromEmail(email) {
  if (!email || !email.includes("@")) return null;
  const d = email.split("@")[1].toLowerCase().trim();
  // Skip generic consumer inboxes — Clearbit returns an empty logo for them,
  // which just wastes a request and flickers to the initials fallback.
  const generic = new Set(["gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.in", "yahoo.co.uk",
    "hotmail.com", "outlook.com", "live.com", "icloud.com", "rediffmail.com", "aol.com", "proton.me", "protonmail.com"]);
  if (generic.has(d)) return null;
  return d;
}

/**
 * <PartnerLogo partner={partnerRow} size="md" />
 * size: "xs" (24px) | "sm" (32px) | "md" (44px) | "lg" (64px) | "xl" (96px)
 */
export default function PartnerLogo({ partner, size = "md", className = "" }) {
  const px = { xs: 24, sm: 32, md: 44, lg: 64, xl: 96 }[size] || 44;
  const radius = size === "xs" ? 6 : size === "sm" ? 8 : 10;
  const [stage, setStage] = useState("upload"); // "upload" → "domain" → "initials"

  // Reset the cascade when the partner swaps (edit modal, logo re-upload).
  useEffect(() => {
    if (partner?.logo_url) setStage("upload");
    else if (domainFromEmail(partner?.email)) setStage("domain");
    else setStage("initials");
  }, [partner?.logo_url, partner?.email, partner?.id]);

  const style = {
    width: px, height: px, borderRadius: radius,
    display: "flex", alignItems: "center", justifyContent: "center",
    overflow: "hidden", flexShrink: 0,
  };

  if (stage === "upload" && partner?.logo_url) {
    // partner.logo_url is already a root-relative path starting with "/api/..."
    // → concat it against the backend ORIGIN (not api.defaults.baseURL, which
    // already carries a trailing "/api" and would make the browser request
    // "/api/api/...logo-image" and 404).
    // No crossOrigin attribute: same-origin cookie flow covers auth; adding it
    // would upgrade the fetch to CORS mode and silently fail on preview builds.
    const origin = (BACKEND_URL || "").replace(/\/$/, "");
    return (
      <div style={style} className={`bg-white ring-1 ring-slate-200 ${className}`} data-testid={`partner-logo-${partner.partner_id}`}>
        <img
          src={`${origin}${partner.logo_url}`}
          alt={partner.company_name}
          onError={() => setStage(domainFromEmail(partner?.email) ? "domain" : "initials")}
          // object-fit: contain keeps the full logo visible (letterboxes landscape/portrait
          // logos inside the square without ever cropping edges). Small padding so the
          // image doesn't touch the rounded border.
          style={{ width: "100%", height: "100%", objectFit: "contain", padding: Math.max(2, Math.round(px * 0.08)) }}
        />
      </div>
    );
  }

  if (stage === "domain") {
    const d = domainFromEmail(partner?.email);
    if (d) {
      return (
        <div style={style} className={`bg-white ring-1 ring-slate-200 ${className}`} data-testid={`partner-logo-${partner.partner_id}`}>
          <img
            src={`https://logo.clearbit.com/${d}`}
            alt={partner.company_name}
            onError={() => setStage("initials")}
            style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
          />
        </div>
      );
    }
  }

  // Final fallback: deterministic initials avatar.
  const key = partner?.company_name || partner?.partner_id || "?";
  const color = PALETTE[hash(key) % PALETTE.length];
  const fontSize = Math.max(10, Math.round(px * 0.38));
  return (
    <div
      style={{ ...style, background: color.bg, color: color.fg, fontSize, fontWeight: 700, letterSpacing: 0.3 }}
      className={`heading-font ${className}`}
      data-testid={`partner-logo-${partner?.partner_id || "unknown"}`}
      aria-label={partner?.company_name}
    >
      {initials(key)}
    </div>
  );
}
